/* eslint-disable @typescript-eslint/no-unused-vars */
'use client';

import * as React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import { DayPicker } from 'react-day-picker';
import { enUS, fr, de, sk, cs } from 'date-fns/locale';
import { format } from 'date-fns';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { buttonVariants } from '@eliadmin/core/src/components/ui/button';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

export const localeMapping: Record<string, any> = {
  'en-US': enUS,
  'fr-FR': fr,
  'de-DE': de,
  sk: sk,
  'sk-SK': sk,
  cs: cs,
  'cs-CZ': cs,
  // Add other locales as needed
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps & { fromYear?: number; toYear?: number }) {
  const localeCode = useLocaleStore(s => s.currentLocale);
  const locale = localeMapping[localeCode] || enUS; // Default to enUS if locale not found
  const { fromYear, toYear, ...dayPickerProps } = props;
  const currentYear = new Date().getFullYear();
  const startMonth = new Date(fromYear ?? currentYear - 10, 0);
  const endMonth = new Date(toYear ?? currentYear + 10, 11);

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      locale={locale} // Use the date-fns locale object
      captionLayout="dropdown"
      navLayout="after"
      startMonth={startMonth}
      endMonth={endMonth}
      className={cn('p-3', className)}
      classNames={{
        months: 'flex flex-col gap-4 sm:flex-row',
        month: 'space-y-4',
        month_caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'hidden',
        dropdowns: 'flex items-center justify-center gap-2 text-sm font-medium',
        dropdown: 'rounded-md border border-black/10 bg-white px-2 py-1 text-sm outline-none',
        nav: 'flex items-center gap-1',
        button_previous: cn(
          buttonVariants({ variant: 'outline' }),
          'absolute left-1 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        button_next: cn(
          buttonVariants({ variant: 'outline' }),
          'absolute right-1 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        month_grid: 'w-full border-collapse space-y-1',
        weekdays: 'flex',
        weekday: 'text-muted-foreground w-8 rounded-md font-normal text-[0.8rem]',
        week: 'mt-2 flex w-full',
        day: cn(
          'relative p-0 text-center text-sm focus-within:relative focus-within:z-20',
          props.mode === 'range'
            ? '[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md'
            : '[&:has([aria-selected])]:rounded-md',
        ),
        day_button: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-8 w-8 p-0 font-normal aria-selected:opacity-100',
        ),
        range_start: 'day-range-start',
        range_end: 'day-range-end',
        selected:
          'bg-black text-white hover:bg-black/90 hover:text-white focus:bg-black focus:text-white',
        today: 'bg-primary text-white',
        outside: 'text-muted-foreground opacity-50',
        disabled: 'text-muted-foreground opacity-50',
        range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) =>
          orientation === 'left' ? (
            <ChevronLeftIcon className="h-4 w-4" />
          ) : (
            <ChevronRightIcon className="h-4 w-4" />
          ),
      }}
      weekStartsOn={props.weekStartsOn ?? 1}
      formatters={{
        formatWeekdayName: date => format(date, 'EE', { locale }),
      }}
      {...dayPickerProps}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
